import { Link } from "react-router-dom";
import { useGuidance } from "./GuidanceProvider";
import { ArrowRight, X } from "lucide-react";

/**
 * Contextual guidance banner — shows a short message + CTA when relevant.
 * 
 * Props:
 *  - hintId: unique ID for this hint (used for dismissal tracking)
 *  - message: 1-2 line guidance message
 *  - cta: button label
 *  - href: link destination
 *  - icon: optional lucide icon component
 *  - show: boolean override (if false, banner won't render regardless)
 *  - onDismiss: optional callback
 */
export function GuidanceBanner({ hintId, message, cta, href, icon: Icon, show = true, onDismiss }) {
  const { state, dismissHint } = useGuidance();

  if (!show) return null;
  if (!state) return null;
  if (state.dismissed_hints?.includes(hintId)) return null;

  const handleDismiss = () => {
    dismissHint(hintId);
    onDismiss?.();
  };

  return (
    <div
      className="relative flex items-start gap-3 bg-primary/5 border border-primary/10 rounded-xl px-4 py-3.5 mb-4"
      data-testid={`guidance-banner-${hintId}`}
    >
      {Icon && (
        <div className="mt-0.5">
          <Icon className="w-4 h-4 text-primary shrink-0" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground leading-relaxed">{message}</p>
        {cta && href && (
          <Link
            to={href}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 mt-1.5 transition-colors"
            data-testid={`guidance-cta-${hintId}`}
          >
            {cta}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      <button
        onClick={handleDismiss}
        className="text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0 mt-0.5"
        data-testid={`dismiss-${hintId}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
